import React, { lazy } from 'react'

import { getUrlParam, urlHasParam } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import { Redirect, Switch } from 'react-router-dom'

import { ACCOUNT_ROUTES } from 'App/constants'
import PrivateRoute from 'App/PrivateRoute'
import { POINTS_HISTORY_NEW_API } from 'constants/optimizely'
import {
  StoreActivation as PayLaterStoreActivation,
  StoreActivationInfo as PayLaterStoreActivationInfo,
} from 'features/klarna'

import { HMPayOverview } from './hm-pay'
import { HubOverview } from './hub'

const AllPurchasesOverview = lazy(
  () => import(/* webpackChunkName: "purchases" */ './purchases/components/AllPurchasesOverview'),
)

const OrderDetailsPage = lazy(
  () => import(/* webpackChunkName: "order-details" */ './purchases/components/OrderDetailsPage'),
)

const PointsHistoryLegacy = lazy(
  () => import(/* webpackChunkName: "points-history-legacy" */ './points-history-legacy'),
)
const PointsHistory = lazy(
  () => import(/* webpackChunkName: "points-history" */ './points-history'),
)

const PointsHistoryConditional: React.FC<{}> = () => {
  const [decision] = useDecision(POINTS_HISTORY_NEW_API)
  if (decision.enabled) {
    return <PointsHistory />
  }
  return <PointsHistoryLegacy />
}

const RewardDetailPage = lazy(
  () => import(/* webpackChunkName: "reward-detail" */ './my-rewards/components/RewardDetailPage'),
)

const DigitalStylist = lazy(
  () => import(/* webpackChunkName: "digital-stylist" */ './digital-stylist'),
)

const InviteFriendSharePage = lazy(
  () =>
    import(
      /* webpackChunkName: "invite-friend-share" */ './invite-friend/components/InviteFriendSharePage'
    ),
)
const HelpUsImprovePage = lazy(
  () => import(/* webpackChunkName: "help-us-improve" */ './help-us-improve'),
)
const InviteFriendInfoPage = lazy(
  () =>
    import(
      /* webpackChunkName: "invite-friend-info" */ './invite-friend/components/InviteFriendInfoPage'
    ),
)

const AllRewardsList = lazy(
  () =>
    import(
      /* webpackChunkName: "all-rewards-list" */ './my-rewards/components/MyRewardsList/components/AllRewardsList'
    ),
)

const SettingsPage = lazy(() => import(/* webpackChunkName: "settings" */ './settings'))

const indexRoute: React.FC = () =>
  urlHasParam('state') && !urlHasParam('error') ? (
    <Redirect to={getUrlParam('state')} />
  ) : (
    <HubOverview />
  )

const privateRoutes = [
  { path: ACCOUNT_ROUTES.Purchases.path, component: AllPurchasesOverview, exact: true },
  {
    path: `${ACCOUNT_ROUTES.Purchases.path}/:orderType/:orderId`,
    component: OrderDetailsPage,
    exact: true,
  },
  { path: ACCOUNT_ROUTES.HMPay.path, component: HMPayOverview, exact: true },
  {
    path: ACCOUNT_ROUTES.PayLaterStoreActivation.path,
    component: PayLaterStoreActivation,
    exact: true,
  },
  {
    path: ACCOUNT_ROUTES.PayLaterStoreActivationInfo.path,
    component: PayLaterStoreActivationInfo,
    exact: true,
  },
  { path: ACCOUNT_ROUTES.PointsHistory.path, component: PointsHistoryConditional, exact: true },
  { path: ACCOUNT_ROUTES.Settings.path, component: SettingsPage },
  { path: ACCOUNT_ROUTES.RewardDetail.path, component: RewardDetailPage },
  { path: ACCOUNT_ROUTES.DigitalStylist.path, component: DigitalStylist },
  {
    path: ACCOUNT_ROUTES.InviteFriend.path,
    component: InviteFriendSharePage,
    exact: true,
  },
  {
    path: ACCOUNT_ROUTES.HelpUs.path,
    component: HelpUsImprovePage,
    exact: true,
  },
  {
    path: ACCOUNT_ROUTES.InviteFriendInfo.path,
    component: InviteFriendInfoPage,
    exact: false,
  },
  {
    path: ACCOUNT_ROUTES.AllRewardsList.path,
    component: AllRewardsList,
  },
]

const Account: React.FC = () => {
  return (
    <Switch>
      <PrivateRoute exact path={ACCOUNT_ROUTES.AccountOverview.path} render={indexRoute} />
      {privateRoutes.map((route) => (
        <PrivateRoute
          exact={route.exact}
          key={route.path}
          path={route.path}
          component={route.component}
        />
      ))}
    </Switch>
  )
}

export default Account
